import { useEffect } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { userRolesState, userInfoState, UserInfo } from "store";
import { MSGraphAuth } from "service/GraphService";
import { ProjectParams } from "config/GraphConfig";
import api from "service/api";
import {
  decodeJWT,
  extractRolesFromToken,
  isTokenExpired,
} from "library/utils/jwtUtils";

// API response type for GET /user/info
interface UserInfoApiResponse {
  userId: string;
  userEmail: string;
  userFullName: string;
  userPhoneNumber: string;
}

/**
 * Custom hook to extract and store user roles from the bearer token
 * This hook should be called once at the app level to initialize user roles
 */
export function useUserRoles(skipApiCall = false) {
  const setUserRoles = useSetAtom(userRolesState);
  const setUserInfo = useSetAtom(userInfoState);

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        // Get the access token
        const authResponse = await MSGraphAuth.getAccessToken(ProjectParams);

        if (!authResponse || !authResponse.accessToken) {
          // console.warn("No access token available");
          setUserRoles([]);
          setUserInfo(null);
          return;
        }

        // Decode the JWT token
        const tokenPayload = decodeJWT(authResponse.accessToken);

        if (!tokenPayload) {
          // console.error("Failed to decode JWT token");
          setUserRoles([]);
          setUserInfo(null);
          return;
        }

        // Check if token is expired
        if (isTokenExpired(tokenPayload)) {
          // console.warn("Token is expired");
          setUserRoles([]);
          setUserInfo(null);
          return;
        }

        // Extract roles from token
        const roles = extractRolesFromToken(tokenPayload);
        setUserRoles(roles);

        // Store basic user info from token
        const basicUserInfo = {
          roles,
          email:
            tokenPayload.email ||
            tokenPayload.upn ||
            tokenPayload.preferred_username,
          name: tokenPayload.name,
          userId: tokenPayload.sub || tokenPayload.oid,
          exp: tokenPayload.exp,
        };

        // Skip API call if we're on the unauthorized page to avoid infinite loop
        if (skipApiCall) {
          setUserInfo(basicUserInfo);
          return;
        }

        // Fetch additional user info from API
        try {
          const userInfoResponse = await api.get("/user/info");
          const apiData = userInfoResponse.data as UserInfoApiResponse;

          // console.log("=== User Info from API (GET /user/info) ===");
          // console.log(JSON.stringify(apiData, null, 2));
          // console.log("===========================================");

          // Merge API data with token data
          const mergedUserInfo: UserInfo = {
            ...basicUserInfo,
            userEmail: apiData.userEmail,
            userFullName: apiData.userFullName,
            userPhoneNumber: apiData.userPhoneNumber,
            // Override userId with the one from API if available
            userId: apiData.userId || basicUserInfo.userId,
          };

          setUserInfo(mergedUserInfo);
        } catch (apiError) {
          console.error("Error fetching user info from API:", apiError);
          // Fallback to token data only
          setUserInfo(basicUserInfo);
        }

        // console.log("User roles loaded:", roles);
      } catch (error) {
        // console.error("Error loading user roles:", error);
        setUserRoles([]);
        setUserInfo(null);
      }
    };

    loadUserRoles();
  }, [setUserRoles, setUserInfo, skipApiCall]);
}

/**
 * Hook to check if the user has a specific role
 * @param role - The role to check
 * @returns true if user has the role, false otherwise
 */
export function useHasRole(role: string): boolean {
  const userRoles = useAtomValue(userRolesState);
  return userRoles.includes(role);
}

/**
 * Hook to check if the user has any of the specified roles
 * @param roles - Array of roles to check
 * @returns true if user has any of the roles, false otherwise
 */
export function useHasAnyRole(roles: string[]): boolean {
  const userRoles = useAtomValue(userRolesState);
  return roles.some((role) => userRoles.includes(role));
}

/**
 * Hook to check if the user has all of the specified roles
 * @param roles - Array of roles to check
 * @returns true if user has all of the roles, false otherwise
 */
export function useHasAllRoles(roles: string[]): boolean {
  const userRoles = useAtomValue(userRolesState);
  return roles.every((role) => userRoles.includes(role));
}

/**
 * Hook to get current user information
 * @returns UserInfo object or null if not loaded
 */
export function useUserInfo() {
  return useAtomValue(userInfoState);
}
